import { useCallback, useState, type ReactNode } from "react";

import { Badge, Button } from "../../../shared/ui";
import { controlClassName } from "../../../shared/ui/controlClassName";
import { formatDateTime } from "../users/format";
import type { OperatorDetail } from "./accessApi";
import type { AccessEnv } from "./accessEnv";
import { operatorName, permissionLabel, roleLabel } from "./accessFormat";
import { ChangeReasonSheet } from "./ChangeReasonSheet";
import { LoadGate } from "./LoadGate";
import {
  describeChange,
  roleNameLookup,
  type PendingChange,
} from "./operatorChanges";
import { useAccessLoad } from "./useAccessLoad";
import { useAccessT } from "./useAccessT";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <dt className="text-caption text-fg-muted">{label}</dt>
      <dd className="min-w-0 break-words text-body text-fg">{children}</dd>
    </div>
  );
}

/** One operator: identity, roles with assign/remove, effective permissions. */
export function OperatorPage({
  env,
  operatorId,
  token,
  onBack,
  onNavigate,
}: {
  env: AccessEnv;
  operatorId: string;
  token: number;
  /** Present on narrow screens, where detail replaces the list. */
  onBack?: () => void;
  onNavigate: (href: string) => void;
}) {
  const t = useAccessT();
  const load = useCallback(
    () => env.api.operator(operatorId),
    [env.api, operatorId],
  );
  const { state, retry } = useAccessLoad(load, token);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {onBack && (
        <Button variant="ghost" size="sm" onClick={onBack}>
          {t("admin.access.back")}
        </Button>
      )}
      <LoadGate state={state} retry={retry}>
        {(operator) => (
          <OperatorBody env={env} operator={operator} onNavigate={onNavigate} />
        )}
      </LoadGate>
    </div>
  );
}

function OperatorBody({
  env,
  operator,
  onNavigate,
}: {
  env: AccessEnv;
  operator: OperatorDetail;
  onNavigate: (href: string) => void;
}) {
  const t = useAccessT();
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [pickedRole, setPickedRole] = useState("");
  const roleName = roleNameLookup(env.roles, t);
  const assignedIds = operator.roles.map((role) => role.role_id);
  const assignable = env.roles.filter(
    (role) => role.active && !assignedIds.includes(role.role_id),
  );
  const description =
    pending === null
      ? null
      : describeChange(pending, operator, roleName, env.api, t);
  const auditHref = `/admin/audit?operator=${encodeURIComponent(operator.operator_id)}`;

  return (
    <article className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="text-title text-fg">{operatorName(operator)}</h2>
        <span className="flex items-center gap-2 text-footnote text-fg-muted">
          <Badge tone={operator.active ? "success" : "neutral"}>
            {operator.active
              ? t("admin.access.operator.active")
              : t("admin.access.operator.inactive")}
          </Badge>
          {operator.is_self && t("admin.access.operator.self")}
        </span>
      </header>
      <dl className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
        {operator.subject !== undefined && (
          <Field label={t("admin.access.operator.subject")}>
            <code className="break-all font-mono text-footnote">
              {operator.subject}
            </code>
          </Field>
        )}
        {operator.linked_user_id !== null && (
          <Field label={t("admin.access.operator.linkedUser")}>
            <a
              href={`/admin/users/${encodeURIComponent(operator.linked_user_id)}`}
              className="break-all font-mono text-footnote text-accent-fg"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                event.preventDefault();
                onNavigate(
                  `/admin/users/${encodeURIComponent(operator.linked_user_id ?? "")}`,
                );
              }}
            >
              {operator.linked_user_id}
            </a>
          </Field>
        )}
        <Field label={t("admin.access.operator.lastSignIn")}>
          {operator.last_sign_in_at === null
            ? t("admin.access.operator.never")
            : formatDateTime(operator.last_sign_in_at)}
        </Field>
        <Field label={t("admin.access.operator.sessions")}>
          {String(operator.active_sessions)}
        </Field>
      </dl>

      <section className="flex flex-col gap-2">
        <h3 className="text-footnote font-semibold text-fg-muted">
          {t("admin.access.operator.roles")}
        </h3>
        {operator.roles.length === 0 ? (
          <p className="text-body text-fg-muted">
            {t("admin.access.operator.noRoles")}
          </p>
        ) : (
          <ul className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
            {operator.roles.map((role) => (
              <li
                key={role.role_id}
                className="flex min-h-11 items-center justify-between gap-2 py-1"
              >
                <span className="text-body text-fg">
                  {roleLabel(t, role.role_id, role.display_name)}
                </span>
                {env.canManage && (
                  <Button
                    variant="dangerSoft"
                    size="sm"
                    onClick={() => {
                      setPending({ kind: "unassign", roleId: role.role_id });
                    }}
                  >
                    {t("admin.access.role.unassign")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {env.canManage && operator.active && (
          <div className="flex flex-col gap-2">
            <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
              {t("admin.access.role.assign")}
              <select
                className={controlClassName}
                value={pickedRole}
                onChange={(event) => {
                  setPickedRole(event.target.value);
                }}
              >
                <option value="">
                  {t("admin.access.role.assign.placeholder")}
                </option>
                {assignable.map((role) => (
                  <option
                    key={role.role_id}
                    value={role.role_id}
                    disabled={!role.grantable}
                  >
                    {roleLabel(t, role.role_id, role.display_name)}
                    {role.grantable
                      ? ""
                      : ` (${t("admin.access.role.notGrantable")})`}
                  </option>
                ))}
              </select>
            </label>
            <Button
              variant="secondary"
              disabled={pickedRole === ""}
              onClick={() => {
                setPending({ kind: "assign", roleId: pickedRole });
              }}
            >
              {t("admin.access.role.assign.submit")}
            </Button>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-footnote font-semibold text-fg-muted">
          {t("admin.access.operator.permissions")}
        </h3>
        {operator.effective_permissions.length === 0 ? (
          <p className="text-body text-fg-muted">
            {t("admin.access.operator.noPermissions")}
          </p>
        ) : (
          <ul className="divide-y divide-line-subtle rounded-xl bg-raised px-4 shadow-card">
            {operator.effective_permissions.map((id) => (
              <li key={id} className="flex flex-col py-2">
                <span className="text-body text-fg">
                  {permissionLabel(t, id)}
                </span>
                <code className="font-mono text-caption text-fg-muted">
                  {id}
                </code>
              </li>
            ))}
          </ul>
        )}
      </section>

      <nav className="flex flex-col gap-2">
        <a
          href={auditHref}
          className="inline-flex min-h-11 items-center text-body font-medium text-accent-fg"
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey) return;
            event.preventDefault();
            onNavigate(auditHref);
          }}
        >
          {t("admin.access.operator.auditTrail")}
        </a>
        {env.canManage && !operator.is_self && (
          <Button
            variant={operator.active ? "dangerSoft" : "secondary"}
            onClick={() => {
              setPending({ kind: operator.active ? "deactivate" : "activate" });
            }}
          >
            {operator.active
              ? t("admin.access.operator.deactivate")
              : t("admin.access.operator.activate")}
          </Button>
        )}
      </nav>

      {description !== null && (
        <ChangeReasonSheet
          env={env}
          title={description.title}
          summary={description.summary}
          warning={description.warning}
          run={description.run}
          onClose={() => {
            setPending(null);
            setPickedRole("");
          }}
        />
      )}
    </article>
  );
}
